/**
 * Applies the blanket-order plan to the table. Renders nothing.
 *
 * A component rather than a hook for the same reason as `AutoMatcher`: the writer it needs lives
 * after `RfqPage`'s loading/error early-returns, so a hook could not be declared unconditionally
 * at the top of that component. Same latest-ref pattern, same fire-once discipline.
 *
 * ---- Fire-once, per line, per SELECTION ----
 *
 * `AutoMatcher` remembers lines; this remembers every line + rate + supplier it has written for
 * the orders currently ticked (`lib/contractApplier.ts`). Ticking another order must run again —
 * with new lines to price — while the re-render that each write causes must not, and a line the
 * purchaser has since switched back to RFQ must NOT be priced a second time: it used to be, the
 * moment the dropdown changed, because only the last plan's signature was remembered (Akhil,
 * 2026-09-16). Untick and re-tick an order to price its lines again.
 */

import { useEffect, useRef } from 'react'
import type { ContractLineAction } from '../../lib/blanketOrders'
import { actionKey, pendingActions, selectionKey } from '../../lib/contractApplier'

export function ContractApplier({
  plan,
  selection,
  enabled,
  apply,
  onDone,
}: {
  plan: ContractLineAction[]
  /** The ticked orders — the memory below is per selection. */
  selection: readonly string[]
  /** False while contracts are still loading, or while another bulk action owns the table. */
  enabled: boolean
  apply: (plan: ContractLineAction[]) => Promise<void>
  onDone: (summary: { priced: number; withChoices: number }) => void
}) {
  const done = useRef<{ selection: string; keys: Set<string> }>({ selection: '', keys: new Set() })
  const running = useRef(false)
  const latest = useRef({ apply, onDone })
  useEffect(() => {
    latest.current = { apply, onDone }
  })

  useEffect(() => {
    if (!enabled || running.current) return
    const key = selectionKey(selection)
    if (done.current.selection !== key) done.current = { selection: key, keys: new Set() }
    const pending = pendingActions(plan, done.current.keys)
    if (pending.length === 0) return

    for (const a of pending) done.current.keys.add(actionKey(a))
    running.current = true
    void (async () => {
      try {
        await latest.current.apply(pending)
        latest.current.onDone({
          priced: pending.length,
          // Lines where more than one contract offers a rate — the purchaser may want the other.
          withChoices: pending.filter((a) => a.alternatives > 1).length,
        })
      } catch {
        // The batch failed as a whole; nothing was routed. The mutation reports it. Allow a
        // retry by forgetting these — otherwise the selection would look applied.
        for (const a of pending) done.current.keys.delete(actionKey(a))
      } finally {
        running.current = false
      }
    })()
  }, [plan, selection, enabled])

  return null
}
